/**
 * Web-specific i18n entry point.
 *
 * Mobile keeps its own config (./config.ts) untouched. This module:
 *   - declares 10 web namespaces:
 *       marketing-site: common, marketing, legal
 *       web-app:        app, auth, feed, comments, notifications, profile, publish
 *   - statically imports FR/EN/ES/PT JSONs so both the marketing site and the
 *     web-app can server-render without an HTTP backend
 *   - exposes `createWebI18n()` to instantiate a fresh i18next per app (Next.js
 *     App Router friendly), and a default `webI18n` for client-side use.
 *
 * Lazy loading by namespace remains possible: consumers can later attach
 * `i18next-http-backend` if they need to ship locale JSONs from a CDN. The
 * current static-import setup is correct for our bundle sizes.
 */
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

// ── marketing-site namespaces ──────────────────────────────────────────────
import enCommon from './locales/web/en/common.json';
import enLegal from './locales/web/en/legal.json';
import enMarketing from './locales/web/en/marketing.json';
import esCommon from './locales/web/es/common.json';
import esLegal from './locales/web/es/legal.json';
import esMarketing from './locales/web/es/marketing.json';
import frCommon from './locales/web/fr/common.json';
import frLegal from './locales/web/fr/legal.json';
import frMarketing from './locales/web/fr/marketing.json';
import ptCommon from './locales/web/pt/common.json';
import ptLegal from './locales/web/pt/legal.json';
import ptMarketing from './locales/web/pt/marketing.json';

// ── web-app namespaces ─────────────────────────────────────────────────────
import enApp from './locales/web/en/app.json';
import enAuth from './locales/web/en/auth.json';
import enComments from './locales/web/en/comments.json';
import enFeed from './locales/web/en/feed.json';
import enNotifications from './locales/web/en/notifications.json';
import enProfile from './locales/web/en/profile.json';
import esApp from './locales/web/es/app.json';
import esAuth from './locales/web/es/auth.json';
import esComments from './locales/web/es/comments.json';
import esFeed from './locales/web/es/feed.json';
import esNotifications from './locales/web/es/notifications.json';
import esProfile from './locales/web/es/profile.json';
import frApp from './locales/web/fr/app.json';
import frAuth from './locales/web/fr/auth.json';
import frComments from './locales/web/fr/comments.json';
import frFeed from './locales/web/fr/feed.json';
import frNotifications from './locales/web/fr/notifications.json';
import frProfile from './locales/web/fr/profile.json';
import ptApp from './locales/web/pt/app.json';
import ptAuth from './locales/web/pt/auth.json';
import ptComments from './locales/web/pt/comments.json';
import ptFeed from './locales/web/pt/feed.json';
import ptNotifications from './locales/web/pt/notifications.json';
import ptProfile from './locales/web/pt/profile.json';

// ── publish namespace ──────────────────────────────────────────────────────
import enPublish from './locales/web/en/publish.json';
import esPublish from './locales/web/es/publish.json';
import frPublish from './locales/web/fr/publish.json';
import ptPublish from './locales/web/pt/publish.json';

// ── moderation namespace ───────────────────────────────────────────────────
import enModeration from './locales/web/en/moderation.json';
import esModeration from './locales/web/es/moderation.json';
import frModeration from './locales/web/fr/moderation.json';
import ptModeration from './locales/web/pt/moderation.json';

// ── settings namespace ─────────────────────────────────────────────────────
import enSettings from './locales/web/en/settings.json';
import esSettings from './locales/web/es/settings.json';
import frSettings from './locales/web/fr/settings.json';
import ptSettings from './locales/web/pt/settings.json';

export const WEB_NAMESPACES = [
  'common',
  'marketing',
  'legal',
  'app',
  'auth',
  'feed',
  'comments',
  'notifications',
  'profile',
  'publish',
  'moderation',
  'settings',
] as const;
export type WebNamespace = (typeof WEB_NAMESPACES)[number];

export const WEB_DEFAULT_NAMESPACE: WebNamespace = 'common';

export const WEB_SUPPORTED_LANGUAGES = ['fr', 'en', 'es', 'pt'] as const;
export type WebLanguage = (typeof WEB_SUPPORTED_LANGUAGES)[number];

export const WEB_DEFAULT_LANGUAGE: WebLanguage = 'fr';

export const webResources = {
  fr: {
    common: frCommon,
    marketing: frMarketing,
    legal: frLegal,
    app: frApp,
    auth: frAuth,
    feed: frFeed,
    comments: frComments,
    notifications: frNotifications,
    profile: frProfile,
    publish: frPublish,
    moderation: frModeration,
    settings: frSettings,
  },
  en: {
    common: enCommon,
    marketing: enMarketing,
    legal: enLegal,
    app: enApp,
    auth: enAuth,
    feed: enFeed,
    comments: enComments,
    notifications: enNotifications,
    profile: enProfile,
    publish: enPublish,
    moderation: enModeration,
    settings: enSettings,
  },
  es: {
    common: esCommon,
    marketing: esMarketing,
    legal: esLegal,
    app: esApp,
    auth: esAuth,
    feed: esFeed,
    comments: esComments,
    notifications: esNotifications,
    profile: esProfile,
    publish: esPublish,
    moderation: esModeration,
    settings: esSettings,
  },
  pt: {
    common: ptCommon,
    marketing: ptMarketing,
    legal: ptLegal,
    app: ptApp,
    auth: ptAuth,
    feed: ptFeed,
    comments: ptComments,
    notifications: ptNotifications,
    profile: ptProfile,
    publish: ptPublish,
    moderation: ptModeration,
    settings: ptSettings,
  },
} as const;

export interface CreateWebI18nOptions {
  lng?: WebLanguage;
  defaultNS?: WebNamespace;
  fallbackLng?: WebLanguage;
}

/**
 * Create a fresh i18next instance for the web stack. Use this in server
 * components / route handlers where you need request-scoped translations.
 */
export function createWebI18n(options: CreateWebI18nOptions = {}): I18nInstance {
  const instance = i18next.createInstance();
  instance.use(initReactI18next).init({
    resources: webResources,
    lng: options.lng ?? WEB_DEFAULT_LANGUAGE,
    fallbackLng: options.fallbackLng ?? WEB_DEFAULT_LANGUAGE,
    defaultNS: options.defaultNS ?? WEB_DEFAULT_NAMESPACE,
    ns: WEB_NAMESPACES as unknown as string[],
    interpolation: { escapeValue: false },
    returnNull: false,
    initImmediate: false,
  });
  return instance;
}

// IMPORTANT: do NOT eagerly instantiate a default webI18n at module load.
// `i18next.use(initReactI18next)` registers the instance as the global default
// for react-i18next's `useTranslation()` hook. If the mobile bundle pulls in
// this module via the i18n barrel (e.g. `import { ... } from 'i18n'`), the web
// instance becomes the default and mobile screens that call `t('feed.x')`
// resolve against the web 'feed' namespace instead of the mobile flat
// translation. Consumers must call `createWebI18n()` themselves.
